import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/glTransaction.mutation"

import {
  As400TransactionSyncStatus,
  CreateGlTransactionDocument,
  type GlTransactionInsertInput,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateGlTransactionResult {
  createGlTransaction: (input: GlTransactionInsertInput) => Promise<string[] | null>
  isCreating: boolean
}

export const useCreateGlTransaction = (): UseCreateGlTransactionResult => {
  const [{ fetching }, executeMutation] = useMutation(CreateGlTransactionDocument)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const createGlTransaction = useCallback(
    async (input: GlTransactionInsertInput): Promise<string[] | null> => {
      const result = await executeMutation({ input })

      if (result.error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: result.error.message || "Failed to create GL transaction. Please try again.",
        })
        return null
      }

      const output = result.data?.glTransaction.create

      if (output?.__typename === "GlTransactionFailure") {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: output.error.message || "Failed to create GL transaction. Please try again.",
        })
        return null
      }

      if (output?.__typename !== "GlTransactionCreateSuccess") {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "Failed to create GL transaction. Please try again.",
        })
        return null
      }

      const transactions = output.glTransactions
      const isRecurring = transactions.length > 1
      const firstTransaction = transactions[0]
      const hasSyncFailure = transactions.some((tx) => tx.as400SyncStatus === As400TransactionSyncStatus.Failed)

      if (isRecurring) {
        notify({
          type: NotificationType.Success,
          silent: false,
          message: `Recurring GL entries created successfully. ${transactions.length} transactions scheduled from ${input.transactionDate} to ${input.repeatEndDate}.`,
        })
        return transactions.map((tx) => tx.glTransactionId)
      }

      const transactionDate = firstTransaction?.transactionDate ?? input.transactionDate
      const today = new Date().toISOString().split("T")[0]
      const isScheduled = transactionDate > today

      if (isScheduled) {
        notify({
          type: NotificationType.Success,
          silent: false,
          message: `GL Entry scheduled for ${transactionDate}. It will be posted automatically on that date.`,
        })
        return transactions.map((tx) => tx.glTransactionId)
      }

      if (hasSyncFailure) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "Transaction posted in AEROS but failed to sync with AS400.",
        })
        return transactions.map((tx) => tx.glTransactionId)
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        message: `GL Entry posted successfully.${firstTransaction?.referenceNumber ? ` Reference: ${firstTransaction.referenceNumber}` : ""}`,
      })
      return transactions.map((tx) => tx.glTransactionId)
    },
    [executeMutation, notify]
  )

  return {
    createGlTransaction,
    isCreating: fetching,
  }
}
